import React, { useEffect, useState } from 'react'
import {  Duration } from 'luxon'
import { useNavigate } from 'react-router-dom'
import QRCode from 'react-qr-code'
import { useSelector } from 'react-redux'

import useIsMobile from 'common/hooks/useIsMobile'
import ButtonCancelApplication from 'components/Buttons/ButtonCancelApplication/ButtonCancelApplication'
import Summary from 'components/Summary/Summary'
import { AppLinks } from 'enviroment/AppLinks'
import { IMOBywatelIdentityMethodSectionProps } from './MObywatelIdentityPageProps'
import { ChooseIdentityMethodTypes, IChooseIdentityMethodPageProps } from 'pages/ChooseIdentityMethodPage/ChooseIdentityMethodPageProps'
import Button, { AppButtonTypes } from 'components/Buttons/ButtonBase/Button'
import { getAppLinkBasedOnOS } from './helpers'
import { useMObywatelPageLogic } from './hooks/useMObywatelPageLogic'
import { sleep } from 'common/helpers/helpers'
import useTextContents from 'common/hooks/useTextContents'
import analyticsService from 'service/AnalyticsService/AnalyticsService'
import { AppState } from 'store'

import RefreshIcon from '../../assets/icons/refresh-icon.svg'
import './MObywatelIdentityPageStyles.scss'


/**
 * Komponent zwracający tekst z linkiem do pobrania aplikacji mObywatel dostosowanym do konkretnego urządzenia.
 * (Sklep Play - na Androidzie, Apple Store na IOS)
 */
const GetMObywatelAppLink = (): JSX.Element => {
   return (
      <p className='mobywatel-identity-section-download-app-text'>
         Nie masz aplikacji mObywatel?  Masz nieaktualną wersję?&nbsp;
         <br />
         <b>
            <a href={getAppLinkBasedOnOS()} target='_blank' rel="noreferrer">Pobierz lub zaktualizuj za darmo.</a>
         </b>
      </p>
   )
}


const MObywatelIdentitySectionDesktop = (props: IMOBywatelIdentityMethodSectionProps): JSX.Element => {
   const BASE_BUTTON_TEXT = "Skopiuj kod"
   const [buttonText, setButtonText] = useState<string>(BASE_BUTTON_TEXT)
   const { isMobywatelCodeActive, timeToCodeExpiresInSeconds, mObywatelCurrentlyActiveCode, regenerateMObywatelCode, mObywatelIdentityPageTextContents, handleOnPrevButtonClick } = props

   const copyMObywatelCodeToClipboard = async () => {
      navigator.clipboard.writeText(mObywatelCurrentlyActiveCode)
      setButtonText("Skopiowano")
      await sleep(3000)
      setButtonText(BASE_BUTTON_TEXT)
   }

   return (
      <>
         <section className='mobywatel-identity-section-outer-wrapper'>
            <div className='mobywatel-identity-section-header-wrapper'>
               <h1>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.header')}</h1>
               <p>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.mainContent')}</p>
            </div>
            <div className='mobywatel-identity-section-code-scan-instruction-wrapper'>
               <h4>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.instructionHeader')}</h4>
               <ol className='mobywatel-identity-section-code-scan-instruction-list code-scan-instruction-list-desktop'>
                  <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.instruction.step1')}</li>
                  <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.instruction.step2')}</li>
                  <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.instruction.step3')}</li>
               </ol>
            </div>
            <div className='mobywatel-identity-section-qr-code-wrapper-desktop'>
               <div className='qr-code-inner-image-wrapper'>
                  <QRCode value={mObywatelCurrentlyActiveCode ?? ""} />
                  {
                     !isMobywatelCodeActive && (
                        <div className='mobywatel-identity-section-qr-code-refresh-overlay' onClick={() => regenerateMObywatelCode(false)} >
                           
                           <img src={RefreshIcon} alt="refresh" />
                           <p>Odśwież <br /> kod QR</p>
                        </div>
                     )
                  }
               </div>
               <Button 
                  buttonContent={buttonText}
                  disabled={!isMobywatelCodeActive }
                  uiButtonType={AppButtonTypes.PRIMARY}
                  className='mobywatel-identity-section-desktop-copy-qr-code-button-desktop'
                  onClick={() => copyMObywatelCodeToClipboard()}
               />
               {
                  isMobywatelCodeActive ?
                     <h6 className='desktop-copy-qr-code-countdown-text'>{`${mObywatelIdentityPageTextContents('mObywatelIdentityPage.desktop.copyCodeHeader')} ${Duration.fromObject({ seconds: timeToCodeExpiresInSeconds }).toFormat('m:ss')}`}</h6>
                     :
                     <div className='desktop-copy-qr-code-countdown-text-placeholder' />
               }
            </div>
            <GetMObywatelAppLink />

            <Button style={{ textTransform: 'none' }} buttonContent={mObywatelIdentityPageTextContents('buttons.chooseAnotherVerificationMethod')} className='page-application-prev-button' onClick={handleOnPrevButtonClick} />
            <ButtonCancelApplication />
         </section>
         <Summary isExtended={true} />
      </>
   )
}


const MObywatelIdentitySectionMobile = (props: IMOBywatelIdentityMethodSectionProps): JSX.Element => {
   /**
      * FUNKCJA POMOCNICZA - zwraca aktualny tekst który powinien być wyświetlony na przycisku
   */
   const getCurrentButtonText = () => {
      if(isMobywatelCodeActive) {
         return `Skopiuj kod (${Duration.fromObject({ seconds: timeToCodeExpiresInSeconds }).toFormat('m:ss')})`
      }
      return 'Generuj nowy kod'
   }
   
   const { isMobywatelCodeActive, timeToCodeExpiresInSeconds, mObywatelCurrentlyActiveCode, regenerateMObywatelCode, mObywatelIdentityPageTextContents, handleOnPrevButtonClick } = props
   const [buttonText, setButtonText] = useState<string>(() => getCurrentButtonText()) // Po odświeżeniu strony, wracamy do ostatnio zapisanego tekstu
   const [isCopying, setIsCopying] = useState(false)


   useEffect(() => {
      if (!isCopying) {
         setButtonText(getCurrentButtonText())
      }
      
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [ isMobywatelCodeActive, timeToCodeExpiresInSeconds, isCopying ])


   /**
    * Funkcja kopiująca kod mObywatela do schowka i zmieniająca tekst na przycisku na określony czas 
   */
   const copyMObywatelCodeToClipboard = async () => {
      if(isCopying === false) {
         navigator.clipboard.writeText(mObywatelCurrentlyActiveCode)
         setButtonText("Skopiowano do schowka")
         setIsCopying(true)
         await sleep(3000)
         setIsCopying(false)
         setButtonText(getCurrentButtonText())
      }
   }

   return (  
      <section className='mobywatel-identity-section-outer-wrapper'>
         <div className='mobywatel-identity-section-header-wrapper'>
            <h1>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.header')}</h1>
            <p>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.mainContent')}</p>
         </div>
         <div className='mobywatel-identity-section-code-scan-instruction-wrapper'>
            <h4>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.instructionHeader')}</h4>
            <ol className='mobywatel-identity-section-code-scan-instruction-list code-scan-instruction-list-mobile'>
               <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.instruction.step1')}</li>
               <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.instruction.step2')}</li>
               <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.instruction.step3')}</li>
               <li>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.instruction.step4')}</li>
            </ol>
         </div>
         <Button
            uiButtonType={AppButtonTypes.PRIMARY} 
            buttonContent={buttonText} 
            className='mobywatel-identity-section-copy-code-button-mobile'
            onClick={() => {
               isMobywatelCodeActive ? copyMObywatelCodeToClipboard() : regenerateMObywatelCode(false)
            }}
         />
         {
            isMobywatelCodeActive ? (
               <div className='mobywatel-identity-section-manual-copy-code-wrapper-mobile'>
                  <h6>{mObywatelIdentityPageTextContents('mObywatelIdentityPage.mobile.copyCodeHeader')}</h6>
                  <p>{mObywatelCurrentlyActiveCode}</p>
               </div>
            )
            :
            <div className='manual-copy-code-wrapper-mobile-placeholder' />
         }
         <GetMObywatelAppLink />
         <Button style={{ textTransform: 'none' }} buttonContent={mObywatelIdentityPageTextContents('buttons.chooseAnotherVerificationMethod')} className='page-application-prev-button' onClick={handleOnPrevButtonClick} />
         <ButtonCancelApplication />
      </section>
   )
}


const MObywatelIdentityPage = (props: IChooseIdentityMethodPageProps): JSX.Element => {
   const { chooseIdentityMethodType } = props
   const isIdentityPage = Boolean(chooseIdentityMethodType === ChooseIdentityMethodTypes.IDENTITY)
   const analyticsEventTypesNames = useSelector((state: AppState) => state.globalConfigurationState.analyticsEventTypesNames)


   const { timeToCodeExpiresInSeconds, isMobywatelCodeActive, regenerateMObywatelCode, mObywatelCurrentlyActiveCode } = useMObywatelPageLogic(isIdentityPage)
   const isMobileDevice = useIsMobile()
   const navigate = useNavigate()
   const mObywatelIdentityPageTextContents = useTextContents()

   useEffect(() => {
      if(isIdentityPage) {
         analyticsEventTypesNames !== undefined && analyticsService.sendAnalyticEvent(analyticsEventTypesNames.MobywatelIdentificationPageShow)
      }
   }, [])

   
   const handleOnPrevButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      navigate(isIdentityPage ? AppLinks.chooseIdentityMethod : AppLinks.chooseVerificationMethod)
   }

   return (
      <div className='mobywatel-identity-page-main-wrapper'>
         {
            isMobileDevice ? 
               <MObywatelIdentitySectionMobile
                  mObywatelIdentityPageTextContents={mObywatelIdentityPageTextContents}
                  regenerateMObywatelCode={regenerateMObywatelCode}
                  mObywatelCurrentlyActiveCode={mObywatelCurrentlyActiveCode }
                  isMobywatelCodeActive={isMobywatelCodeActive}
                  timeToCodeExpiresInSeconds={timeToCodeExpiresInSeconds}
                  handleOnPrevButtonClick={handleOnPrevButtonClick}
               />
               :
               <MObywatelIdentitySectionDesktop
                  mObywatelIdentityPageTextContents={mObywatelIdentityPageTextContents}
                  regenerateMObywatelCode={regenerateMObywatelCode}
                  mObywatelCurrentlyActiveCode={mObywatelCurrentlyActiveCode}
                  isMobywatelCodeActive={isMobywatelCodeActive}
                  timeToCodeExpiresInSeconds={timeToCodeExpiresInSeconds}   
                  handleOnPrevButtonClick={handleOnPrevButtonClick}
               />
         }
      </div>
   )
}

export default MObywatelIdentityPage
